import BaseComponent from '@/components/base'

const selectors = {
  thumb: '[data-thumb]',
  marker: '[data-thumb-marker]',
  galleryItem: '.product-detail-gallery__item',
}

/**
 * Vertical thumbnail strip in the bottom-right corner (desktop), pinned inside the
 * sticky overlay so it releases when the images end. A blue overlay tracks the
 * scroll continuously: it maps how far the viewport TOP is through each gallery
 * image, sliding down the thumbs and landing exactly on one when an image is
 * scrolled to the top (e.g. after a click).
 */
export default class ProductThumbnails extends BaseComponent {
  static TYPE = 'product-thumbnails'

  thumbs: HTMLElement[]
  marker: HTMLElement | undefined
  galleryItems: HTMLElement[]
  rafId = 0
  onResize: () => void

  constructor(el: HTMLElement) {
    super(el, { watchScroll: true })

    this.thumbs = this.qsa(selectors.thumb) as HTMLElement[]
    this.marker = this.qs(selectors.marker)

    const section = this.el.closest('[data-section-type="product"]')
    this.galleryItems = section
      ? (Array.from(section.querySelectorAll(selectors.galleryItem)) as HTMLElement[])
      : []

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.scrollToImage(index))
    })

    this.onResize = () => this.update()
    window.addEventListener('resize', this.onResize)

    requestAnimationFrame(() => this.update())
  }

  onScroll() {
    if (this.rafId) return
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0
      this.update()
    })
  }

  /** Slide the blue overlay vertically from how far the viewport TOP is through each image. */
  update() {
    if (!this.marker) return

    const items = this.galleryItems.filter(item => item.style.display !== 'none')
    const thumbs = this.thumbs.filter(thumb => thumb.style.display !== 'none')
    if (!items.length || !thumbs.length) return

    let index = 0
    let fraction = 0

    if (items[0].getBoundingClientRect().top > 0) {
      index = 0
      fraction = 0
    }
    else if (items[items.length - 1].getBoundingClientRect().bottom <= 0) {
      index = items.length - 1
      fraction = 0
    }
    else {
      for (let i = 0; i < items.length; i++) {
        const rect = items[i].getBoundingClientRect()
        if (rect.top <= 0 && rect.bottom > 0) {
          index = i
          fraction = Math.min(1, Math.max(0, -rect.top / rect.height))
          break
        }
      }
    }

    const from = thumbs[index]
    const to = thumbs[index + 1] ?? from
    const y = from.offsetTop + (to.offsetTop - from.offsetTop) * fraction

    this.marker.style.width = `${from.offsetWidth}px`
    this.marker.style.height = `${from.offsetHeight}px`
    this.marker.style.transform = `translate(${from.offsetLeft}px, ${y}px)`
  }

  scrollToImage(index: number) {
    this.galleryItems[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  /** Show only the thumbs matching the selected colour (mirrors the gallery). */
  filterByColor(color: string) {
    const target = (color || '').trim().toLowerCase()

    const isVisible = (el: HTMLElement) => {
      const c = (el.dataset.color || '').trim().toLowerCase()
      return !target || !c || c === target
    }

    this.thumbs.forEach(thumb => {
      thumb.style.setProperty('display', isVisible(thumb) ? '' : 'none')
    })

    this.update()
  }

  destroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.rafId) cancelAnimationFrame(this.rafId)

    super.destroy()
  }
}
